import { useFormik } from 'formik';
import Card from '../../components/card/Card';
import P from '../../components/texts/p/P';
import { useEffect, useState } from 'react';
import Input from '../../components/input/Input';
import Button from '../../components/button/Button';
import Icon from '../../components/icon/Icon';
import { SIGNERS_TYPES } from '../../utils/Constants';
import { corporateStructureData, signersData } from '../../types/interfaces';
import {
  getMimeType,
  hasData,
  isSuccessfulResponse,
  removeFileExtension,
} from '../../utils/Utils';
import { saveSignerDocument } from '../../services/ClientService';
import Span from '../../components/texts/span/Span';
import Label from '../../components/texts/label/Label';
import { downloadSignerDocument } from '../../services/ApplicationService';
import { useLocation } from 'react-router-dom';
import SearchableSelect from '../../components/sercheableselect/SearchableSelect';
import { localities } from '../../mocks/Localities';
import MultiSelectDropdown from '../../components/multiselectdropdown/MultiSelectDropdown';
import H from '../../components/texts/h/H';
import styled from 'styled-components';
import theme from '../../styles/theme';

// Constantes de espaciado para el componente Signers
// Usa design tokens del theme para mantener consistencia
const SIGNERS_CONSTANTS = {
  GRID_GAP: theme.spacing['spacing-3'], // 16px - Espacio entre columnas de firmantes
  FIELD_GAP: theme.spacing['spacing-3'], // 16px - Espacio entre campos dentro de un firmante
  BUTTONS_GAP: theme.spacing['spacing-2'], // 8px - Espacio entre botones (tacho y agregar)
  SMALL_GAP: theme.spacing['spacing-0'], // 0px - Gap mínimo para contenedores sin espaciado
  INPUT_MIN_HEIGHT: theme.spacing['spacing-7'], // 48px - Altura mínima del input con botones
  ERROR_MIN_HEIGHT: '20px', // Altura mínima para mensajes de error (no hay token exacto)
  // Offset negativo para alinear verticalmente firmantes adicionales con el primero
  // Este valor compensa pequeñas diferencias de altura en el título o primer campo
  ALIGNMENT_OFFSET: '-4px',
  // Breakpoints del theme
  BREAKPOINT_TABLET: theme.breakpoints.s.size, // 1024px
  BREAKPOINT_MOBILE: theme.breakpoints.xs.size, // 768px
} as const;

const SignersContainer = styled.div<{ hasMultipleSigners: boolean }>`
  ${({ hasMultipleSigners }) => {
    if (!hasMultipleSigners) {
      return `
        display: flex;
        flex-direction: column;
        gap: ${SIGNERS_CONSTANTS.SMALL_GAP};
      `;
    }
    return `
      display: grid;
      /* Usa auto-fit con minmax para que se ajuste automáticamente al espacio disponible */
      /* Mínimo 320px por columna, máximo 1fr (distribuye el espacio equitativamente) */
      /* Si no hay espacio suficiente, los firmantes se apilan automáticamente */
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: ${SIGNERS_CONSTANTS.GRID_GAP};
      align-items: start;
      grid-auto-flow: row;
      grid-auto-rows: min-content;
      width: 100%;
      box-sizing: border-box;
      
      /* En pantallas pequeñas, una sola columna con ancho completo */
      @media (max-width: ${SIGNERS_CONSTANTS.BREAKPOINT_MOBILE}) {
        grid-template-columns: 1fr;
      }
    `;
  }}
`;

// Helper function para calcular el margin-top de firmantes adicionales
// Compensa diferencias de altura para alinear verticalmente con el primer firmante
const getSignerCardMargin = (hasMultipleSigners: boolean, index?: number) => {
  if (!hasMultipleSigners) {
    return 'margin: 0;';
  }
  if (index && index > 0) {
    return `margin-top: ${SIGNERS_CONSTANTS.ALIGNMENT_OFFSET}; margin-left: 0; margin-right: 0; margin-bottom: 0;`;
  }
  return 'margin: 0;';
};

const SignerCard = styled.div<{ hasMultipleSigners: boolean; index?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${SIGNERS_CONSTANTS.FIELD_GAP};
  align-self: start;
  padding: 0;
  box-sizing: border-box;

  ${({ hasMultipleSigners, index }) => {
    if (!hasMultipleSigners) {
      return `
        width: auto;
        max-width: 100%;
        ${getSignerCardMargin(hasMultipleSigners, index)}
      `;
    }
    return `
      width: 100%;
      ${getSignerCardMargin(hasMultipleSigners, index)}
    `;
  }}
`;

// Título del firmante con altura consistente para mantener alineación
const SignerTitle = styled(H)`
  margin: 0;
  padding: 0;
  height: auto;
  line-height: 1.5;
  min-height: 1.5em; /* Altura mínima para mantener consistencia entre firmantes */
  display: block;
  box-sizing: border-box;
`;

// Contenedor base para inputs
// Mantiene ancho fijo para que no se agranden con contenido largo
const InputContainer = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

// Variante del InputContainer para el campo CUIL que necesita flex
const CUILInputContainer = styled(InputContainer)`
  flex: 1;
  min-width: 0;
`;

// Wrapper para cada campo del formulario
// Usa gap 0 para control preciso del espaciado interno
const FieldCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SIGNERS_CONSTANTS.SMALL_GAP};
  align-self: start;
  min-height: fit-content;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  width: 100%;
`;

// Label de campo con altura mínima consistente para mantener alineación
const FieldLabel = styled(P)`
  margin: 0;
  padding: 0;
  min-height: 1.2em; /* Altura mínima para mantener alineación entre columnas */
  line-height: 1.2;
  display: block;
`;

// Span para mensajes de error con altura mínima para mantener alineación
const ErrorSpan = styled(Span)`
  min-height: ${SIGNERS_CONSTANTS.ERROR_MIN_HEIGHT};
  display: block;
`;

// Contenedor para botones (tacho y agregar firmante)
// Usa spacing-2 (8px) como base, pero podría necesitarse spacing-1.5 (12px) en el futuro
const ButtonsWrapper = styled.div`
  display: flex;
  gap: ${SIGNERS_CONSTANTS.BUTTONS_GAP};
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

// Contenedor del campo CUIL con input y botones
const CUILFieldContainer = styled.div`
  display: flex;
  gap: ${SIGNERS_CONSTANTS.BUTTONS_GAP};
  flex-direction: column;
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  @media (max-width: ${SIGNERS_CONSTANTS.BREAKPOINT_MOBILE}) {
    > div:first-child {
      width: 100%;
    }
  }
`;

// Contenedor que agrupa el input CUIL y los botones de acción
// Altura mínima para mantener alineación con otros campos
const InputAndButtonsContainer = styled.div`
  display: flex;
  gap: ${SIGNERS_CONSTANTS.BUTTONS_GAP};
  align-items: center;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  min-height: ${SIGNERS_CONSTANTS.INPUT_MIN_HEIGHT};
  box-sizing: border-box;
`;

// Contenedor para el archivo cargado que mantiene el ancho completo
// No debe afectar el tamaño de otros inputs ni agrandarse
// Tiene ancho fijo igual al InputContainer
const AssetFileContainer = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
`;

// Wrapper interno que limita el ancho del Card
// Este wrapper previene que el Card se expanda
const AssetFileCardWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex: 1 1 0;
`;

// Card interno que muestra el archivo cargado con ancho completo
// Mantiene el mismo ancho que el input de archivo, no se agranda
const AssetFileCard = styled(Card)`
  && {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }
`;

// Contenedor interno del Card para controlar el layout del contenido
// Este contenedor debe tener overflow hidden para truncar el texto
const AssetFileCardContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${SIGNERS_CONSTANTS.BUTTONS_GAP};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

// Label del archivo que permite truncar el texto con puntos suspensivos
// No debe cambiar el tamaño del contenedor
// Es crítico que tenga overflow hidden y text-overflow ellipsis
const AssetFileLabel = styled(Label)`
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  display: block;
  box-sizing: border-box;
`;

interface SignersProps {
  cuit: string | undefined;
  applicationId: string | undefined;
  signersData: any[];
  isApplicationEditable: boolean;
  onChange: Function;
  corporateStructure?: corporateStructureData[] | null;
  requireValidations?: boolean;
  allowEmptySigners?: boolean;
  onUploadPendingFiles?: () => Promise<void>;
}

const Signers: React.FC<SignersProps> = ({
  isApplicationEditable,
  onChange,
  cuit,
  signersData = [
    {
      cuit: '',
      name: '',
      locality: '',
      address: '',
      role: '',
      email: '',
      asset_file_path: '',
      signer_type_code: ['001'],
    },
  ],
  applicationId,
  corporateStructure,
  requireValidations,
  allowEmptySigners,
  onUploadPendingFiles,
}) => {
  const location = useLocation();
  
  // Estado para almacenar archivos pendientes de subir
  const [pendingFiles, setPendingFiles] = useState<Map<number, File>>(new Map());

  useEffect(() => {
    if (
      (location.pathname.includes('/instrumentacion') && !corporateStructure) ||
      corporateStructure?.length === 0
    ) {
      const hasValidLocalSigners = signersFormik.values.some(
        (signer) =>
          signer.cuit &&
          signer.cuit.length === 11 &&
          signer.name &&
          signer.name.trim() !== ''
      );

      // Solo sincronizar si no hay signers locales válidos
      if (!hasValidLocalSigners) {
        const current = JSON.stringify(signersFormik.values);
        const incoming = JSON.stringify(signersData);
        if (current !== incoming) {
          signersFormik.setValues(signersData);
        }
      }
    }
  }, [signersData, corporateStructure]);

  const removeSigner = (index: number) => {
    const updated = signersFormik.values.filter((_, i) => i !== index);
    signersFormik.setValues(updated);
  };

  const shouldIgnoreValidationForSigner = (signer: any) => {
    const cuitSocios = new Set(
      corporateStructure && corporateStructure.map((s) => s.ci)
    );
    return cuitSocios.has(signer.cuit);
  };

  const getFieldError = (index: number, field: string) => {
    const error = signersFormik.errors[index];
    return typeof error === 'object' &&
      error &&
      !Array.isArray(error) &&
      field in error
      ? (error as any)[field]
      : '';
  };

  const validateSignerData = (values: signersData[]) => {
    const errors: any[] = [];

    if (isApplicationEditable) {
      return errors;
    }
    if (values.length === 0 && !allowEmptySigners) {
      errors.push({ general: 'Debe existir al menos un firmante' });
      return errors;
    }

    const shouldValidate =
      requireValidations !== undefined
        ? requireValidations
        : location.pathname.includes('/instrumentacion');

    values.forEach((signer, index) => {
      if (shouldIgnoreValidationForSigner(signer)) return;

      const signerErrors: any = {};

      if (!signer.cuit) signerErrors.cuit = 'El campo CUIL es obligatorio';
      else if (signer.cuit.length !== 11)
        signerErrors.cuit = 'El campo debe tener 11 caracteres';

      if (!signer.name) signerErrors.name = 'El campo Nombre es obligatorio';

      if (shouldValidate) {
        if (!signer.locality)
          signerErrors.locality = 'El campo Localidad es obligatorio';
        if (!signer.address)
          signerErrors.address = 'El campo Domicilio es obligatorio';
        if (!signer.role)
          signerErrors.role = 'El campo Caracter legal es obligatorio';
        if (!signer.email) signerErrors.email = 'El campo email es obligatorio';
        else if (
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(signer.email)
        )
          signerErrors.email = 'Formato de email inválido';
        if (!signer.signer_type_code || signer.signer_type_code.length === 0)
          signerErrors.signer_type_code =
            'Al menos un tipo de firma es obligatorio';
      }

      if (Object.keys(signerErrors).length > 0) errors[index] = signerErrors;
    });
    return errors;
  };

  const signersFormik = useFormik({
    initialValues: signersData,
    validate: validateSignerData,
    validateOnMount: true,
    onSubmit: () => {},
    enableReinitialize: false,
  });

  useEffect(() => {
    const hasErrors = !signersFormik.isValid;
    onChange({ 
      values: signersFormik.values, 
      hasErrors,
      hasPendingFiles: pendingFiles.size > 0,
      uploadPendingFiles
    });
  }, [signersFormik.values, signersFormik.errors, signersFormik.isValid, pendingFiles]);
  
  const handleDeleteFile = (index: number) => {
    // Si el archivo está pendiente, solo quitarlo de pendientes
    if (pendingFiles.has(index)) {
      const newPendingFiles = new Map(pendingFiles);
      newPendingFiles.delete(index);
      setPendingFiles(newPendingFiles);
    }
    // Limpiar el campo en formik
    signersFormik.setFieldValue(`${index}.asset_file_path`, '');
  };

  const addSigner = () => {
    signersFormik.setValues([
      ...signersFormik.values,
      {
        cuit: '',
        name: '',
        locality: '',
        address: '',
        role: '',
        email: '',
        asset_file_path: '',
        signer_type_code: ['001'],
      },
    ]);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Almacenar el archivo en pendientes sin subirlo
      const newPendingFiles = new Map(pendingFiles);
      newPendingFiles.set(index, file);
      setPendingFiles(newPendingFiles);
      
      // Actualizar el estado visual con el nombre del archivo
      signersFormik.setFieldValue(
        `${index}.asset_file_path`,
        file.name
      );
    }
  };
  
  // Función para subir archivos pendientes
  const uploadPendingFiles = async () => {
    const uploadedPaths: { index: number; path: string }[] = [];
    
    for (const [index, file] of pendingFiles.entries()) {
      const fileName = removeFileExtension(file.name);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', index.toString());
      formData.append('fileName', fileName);
      
      const response = await saveSignerDocument(cuit, formData);
      if (hasData(response) && isSuccessfulResponse(response)) {
        uploadedPaths.push({ index, path: response.data });
      } else {
        throw new Error(`Error al subir archivo de bienes del firmante ${index + 1}`);
      }
    }
    
    // Actualizar los paths en formik con los nombres reales del servidor
    uploadedPaths.forEach(({ index, path }) => {
      signersFormik.setFieldValue(`${index}.asset_file_path`, path);
    });
    
    // Limpiar archivos pendientes
    setPendingFiles(new Map());
  };
  
  // Exponer la función de subida al componente padre
  useEffect(() => {
    if (onUploadPendingFiles) {
      (window as any).__signersUploadPendingFiles = uploadPendingFiles;
    }
  }, [pendingFiles, onUploadPendingFiles]);

  const handleViewFile = (filename: string, signerIndex: number) => {
    // Si el archivo está pendiente, descargarlo desde memoria
    const pendingFile = pendingFiles.get(signerIndex);
    if (pendingFile) {
      const url = window.URL.createObjectURL(pendingFile);
      const mimeType = pendingFile.type;
      
      if (
        mimeType.startsWith('application/pdf') ||
        mimeType.startsWith('image/')
      ) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = pendingFile.name;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      return;
    }
    
    // Si no está pendiente, descargarlo del servidor
    downloadSignerDocument(applicationId, cuit, filename).then((response) => {
      if (response.success) {
        const mimeType = getMimeType(filename);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);

        if (
          mimeType.startsWith('application/pdf') ||
          mimeType.startsWith('image/')
        ) {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    });
  };

  const hasMultipleSigners = signersFormik.values.length > 1;
  const hasNoSigners = signersFormik.values.length === 0;

  return (
    <Card>
      <form onSubmit={signersFormik.handleSubmit}>
        {hasNoSigners ? (
          <Card display='flex' justifyContent='flex-start'>
            <Button
              id='signers-add-button-empty'
              variant='tertiary'
              size='md'
              onClick={addSigner}
              disabled={isApplicationEditable}
              icon='add_outline'
              iconAlign='left'
            >
              <span style={{ whiteSpace: 'nowrap' }}>Agregar firmante</span>
            </Button>
          </Card>
        ) : (
          <SignersContainer hasMultipleSigners={hasMultipleSigners}>
            {signersFormik.values.map((signer: any, index: number) => {
            const isLastSigner = index === signersFormik.values.length - 1;
            return (
              <SignerCard key={index} hasMultipleSigners={hasMultipleSigners} index={index}>
                <SignerTitle
                  type='3'
                  variant='neutral'
                  weight='bold'
                  modifier='regular'
                  withMargin={false}
                >
                  Firmante {index + 1}
                </SignerTitle>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    CUIL
                  </FieldLabel>
                  <CUILFieldContainer>
                    <InputAndButtonsContainer>
                      <CUILInputContainer>
                        <Input
                          id={`signer-cuit-input-${index}`}
                          value={signersFormik.values[index]?.cuit}
                          maxLength={11}
                          placeholder='CUIL'
                          type='text'
                          onChange={(e) =>
                            signersFormik.setFieldValue(
                              `[${index}].cuit`,
                              e.target.value
                            )
                          }
                          disabled={isApplicationEditable}
                        />
                      </CUILInputContainer>
                      <ButtonsWrapper>
                        <Button
                          size='md'
                          variant='secondary'
                          onClick={() => removeSigner(index)}
                          disabled={isApplicationEditable}
                        >
                          <Card
                            variant='none'
                            height='26px'
                            margin={{
                              top: '2',
                              bottom: '2',
                            }}
                          >
                            <Icon
                              variant='neutral'
                              name='delete_outline'
                              cursor='pointer'
                            />
                          </Card>
                        </Button>
                        {isLastSigner && (
                          <Button
                            id='signers-add-button'
                            variant='tertiary'
                            size='md'
                            onClick={addSigner}
                            disabled={isApplicationEditable}
                            icon='add_outline'
                            iconAlign='left'
                          >
                            <span style={{ whiteSpace: 'nowrap' }}>Agregar firmante</span>
                          </Button>
                        )}
                      </ButtonsWrapper>
                    </InputAndButtonsContainer>
                    <InputContainer>
                      <ErrorSpan variant='danger' type='2'>
                        {getFieldError(index, 'cuit')}
                      </ErrorSpan>
                    </InputContainer>
                  </CUILFieldContainer>
                </FieldCardWrapper>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    Nombre
                  </FieldLabel>
                  <InputContainer>
                    <Input
                      id={`signer-name-input-${index}`}
                      value={signersFormik.values[index]?.name}
                      type='text'
                      maxLength={255}
                      placeholder='Nombre'
                      onChange={(e) =>
                        signersFormik.setFieldValue(
                          `[${index}].name`,
                          e.target.value
                        )
                      }
                      disabled={isApplicationEditable}
                    />
                    <ErrorSpan variant='danger' type='2'>
                      {getFieldError(index, 'name')}
                    </ErrorSpan>
                  </InputContainer>
                </FieldCardWrapper>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    Mail
                  </FieldLabel>
                  <InputContainer>
                    <Input
                      id={`signer-email-input-${index}`}
                      value={signersFormik.values[index]?.email}
                      maxLength={255}
                      placeholder='mail@mail.com'
                      type='text'
                      onChange={(e) =>
                        signersFormik.setFieldValue(
                          `[${index}].email`,
                          e.target.value
                        )
                      }
                      disabled={isApplicationEditable}
                    />
                    <ErrorSpan variant='danger' type='2'>
                      {!shouldIgnoreValidationForSigner(signer) &&
                        getFieldError(index, 'email')}
                    </ErrorSpan>
                  </InputContainer>
                </FieldCardWrapper>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    Localidad
                  </FieldLabel>
                  <InputContainer>
                    <SearchableSelect
                      id={`signer-locality-input-${index}`}
                      placeholder='Buscar localidad...'
                      options={localities}
                      value={signersFormik.values[index]?.locality || ''}
                      onChange={(value) =>
                        signersFormik.setFieldValue(`[${index}].locality`, value)
                      }
                      disabled={isApplicationEditable}
                    />
                    <ErrorSpan variant='danger' type='2'>
                      {!shouldIgnoreValidationForSigner(signer) &&
                        getFieldError(index, 'locality')}
                    </ErrorSpan>
                  </InputContainer>
                </FieldCardWrapper>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    Domicilio
                  </FieldLabel>
                  <InputContainer>
                    <Input
                      id={`signer-address-input-${index}`}
                      value={signersFormik.values[index]?.address || ''}
                      type='text'
                      maxLength={255}
                      placeholder='Calle y número'
                      onChange={(e) =>
                        signersFormik.setFieldValue(
                          `[${index}].address`,
                          e.target.value
                        )
                      }
                      disabled={isApplicationEditable}
                    />
                    <ErrorSpan variant='danger' type='2'>
                      {!shouldIgnoreValidationForSigner(signer) &&
                        getFieldError(index, 'address')}
                    </ErrorSpan>
                  </InputContainer>
                </FieldCardWrapper>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    Caracter legal
                  </FieldLabel>
                  <InputContainer>
                    <Input
                      id={`signer-role-input-${index}`}
                      value={signersFormik.values[index]?.role || ''}
                      type='text'
                      maxLength={255}
                      placeholder='Caracter legal'
                      onChange={(e) =>
                        signersFormik.setFieldValue(
                          `[${index}].role`,
                          e.target.value
                        )
                      }
                      disabled={isApplicationEditable}
                    />
                    <ErrorSpan variant='danger' type='2'>
                      {!shouldIgnoreValidationForSigner(signer) &&
                        getFieldError(index, 'role')}
                    </ErrorSpan>
                  </InputContainer>
                </FieldCardWrapper>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    Tipo de firma
                  </FieldLabel>
                  <InputContainer>
                    <MultiSelectDropdown
                      options={SIGNERS_TYPES.map((type) => ({
                        label: type.label,
                        value: type.id,
                      }))}
                      selectedValues={
                        signersFormik.values[index]?.signer_type_code || ['001']
                      }
                      onChange={(selectedCodes) => {
                        signersFormik.setFieldValue(
                          `[${index}].signer_type_code`,
                          selectedCodes
                        );
                      }}
                      placeholder='Elegir'
                      disabled={isApplicationEditable}
                      singleSelect={false}
                    />
                    <ErrorSpan variant='danger' type='2'>
                      {!shouldIgnoreValidationForSigner(signer) &&
                        getFieldError(index, 'signer_type_code')}
                    </ErrorSpan>
                  </InputContainer>
                </FieldCardWrapper>

                <FieldCardWrapper>
                  <FieldLabel type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                    Archivo de bienes
                  </FieldLabel>
                  <InputContainer>
                    {signer.asset_file_path ? (
                      <AssetFileContainer>
                        <AssetFileCardWrapper>
                          <AssetFileCard
                            variant='neutral'
                            modifier='inverted'
                            height='48px'
                            spacing={{ left: '2', right: '2' }}
                            borderRadiusAmount='3'
                            border
                          >
                            <AssetFileCardContent>
                              <AssetFileLabel
                                variant='neutral'
                                modifier='strong'
                                type='2'
                              >
                                {signer.asset_file_path}
                              </AssetFileLabel>
                              <Icon
                                name='view_outline'
                                variant='neutral'
                                height={44}
                                cursor='pointer'
                                onClick={() => handleViewFile(signer.asset_file_path, index)}
                              />
                              <Icon
                                name='delete_outline'
                                variant='neutral'
                                height={44}
                                cursor='pointer'
                                onClick={() => handleDeleteFile(index)}
                              />
                            </AssetFileCardContent>
                          </AssetFileCard>
                        </AssetFileCardWrapper>
                      </AssetFileContainer>
                    ) : (
                      <Card>
                        <Input
                          id={`signers-file_input-${index}`}
                          type='file'
                          size='md'
                          icons={{ iconRight: 'upload_outline' }}
                          onChange={(e) => handleFileChange(e, index)}
                          accept='.pdf, .jpg, .jpeg, .png, .xls, .xlsx, .doc, .docx'
                          disabled={isApplicationEditable}
                        />
                        <ErrorSpan variant='danger' type='2'>
                          {!shouldIgnoreValidationForSigner(signer) &&
                            getFieldError(index, 'asset_file_path')}
                        </ErrorSpan>
                      </Card>
                    )}
                  </InputContainer>
                </FieldCardWrapper>
              </SignerCard>
            );
          })}
          </SignersContainer>
        )}
      </form>
    </Card>
  );
};

export default Signers;
